/**
 * Custom Pulse Report Templates
 * Flexible templates for specific use cases
 */

import { PulseReportTemplate } from "./types";

export const ONBOARDING_PROGRESS: PulseReportTemplate = {
  id: "onboarding-progress",
  name: "Onboarding Progress Report",
  reportType: "custom",
  description: "Track new user onboarding completion and next steps",
  purpose:
    "Guide new users through setup by showing progress and suggesting next actions.",
  icon: "FlagOutlined",
  color: "#10b981",
  suggestedHtmlTemplateId: "account-health-score",
  suggestedSubject: "Your Setup Progress: {{onboarding_percent}}% Complete",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 1,
    time: "10:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "New users in onboarding phase",
  expectedOutcomes: [
    "Higher onboarding completion",
    "Faster time to value",
    "Reduced early churn",
    "Clear next steps for users",
  ],
  bestPractices: [
    "Show visual progress bar",
    "Highlight completed milestones",
    "Suggest one clear next action",
    "Celebrate progress made",
  ],
  keyMetrics: [
    "Onboarding completion %",
    "Steps completed",
    "Next recommended action",
    "Time since signup",
  ],
  tags: ["custom", "onboarding", "progress", "setup", "activation"],
  bestFor: ["New user activation", "Onboarding campaigns", "Setup completion"],
  difficulty: "intermediate",
  setupTime: "15 minutes",
  previewDescription: "Onboarding progress tracker with next steps",
};

export const CHATBOT_ROI_REPORT: PulseReportTemplate = {
  id: "chatbot-roi-report",
  name: "Chatbot ROI Report",
  reportType: "custom",
  description: "Demonstrate chatbot value with ROI metrics",
  purpose:
    "Show decision makers the concrete business value and ROI of their chatbot investment.",
  icon: "DollarOutlined",
  color: "#f59e0b",
  suggestedHtmlTemplateId: "chatbot-roi",
  suggestedSubject: "Your Chatbot ROI: {{savings_amount}} saved this month",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: true,
    analytics: false,
    team: false,
  },
  targetAudience: "Business owners and decision makers",
  expectedOutcomes: [
    "Clear ROI demonstration",
    "Budget justification",
    "Increased plan upgrades",
    "Stakeholder buy-in",
  ],
  bestPractices: [
    "Calculate cost savings vs manual support",
    "Show time saved per conversation",
    "Include industry benchmarks",
    "Project annual impact",
  ],
  keyMetrics: [
    "Conversations handled",
    "Hours saved",
    "Cost per conversation",
    "Total savings",
    "ROI percentage",
  ],
  tags: ["custom", "roi", "value", "savings", "business"],
  bestFor: ["Executive reporting", "Budget justification", "Value demonstration"],
  difficulty: "advanced",
  setupTime: "20 minutes",
  previewDescription: "ROI and cost savings analysis",
};

export const CONVERSATION_INSIGHTS: PulseReportTemplate = {
  id: "conversation-insights",
  name: "Conversation Insights Report",
  reportType: "custom",
  description: "Deep dive into conversation patterns and user intent",
  purpose:
    "Provide actionable insights from conversation data to improve chatbot effectiveness.",
  icon: "MessageOutlined",
  color: "#8b5cf6",
  suggestedHtmlTemplateId: "monthly-analytics",
  suggestedSubject: "Conversation Insights - {{total_conversations}} analyzed",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Chatbot managers and analysts",
  expectedOutcomes: [
    "Improved bot responses",
    "Gap identification",
    "User intent understanding",
    "Content optimization",
  ],
  bestPractices: [
    "Highlight top user intents",
    "Show failed conversation patterns",
    "Suggest response improvements",
    "Include sentiment analysis",
  ],
  keyMetrics: [
    "Top intents",
    "Failed conversations",
    "Common questions",
    "Sentiment score",
    "Drop-off points",
  ],
  tags: ["custom", "conversations", "insights", "analytics", "optimization"],
  bestFor: ["Bot optimization", "Content strategy", "User research"],
  difficulty: "advanced",
  setupTime: "15 minutes",
  previewDescription: "Deep conversation analytics and insights",
};

export const LEAD_GENERATION_REPORT: PulseReportTemplate = {
  id: "lead-generation-report",
  name: "Lead Generation Report",
  reportType: "custom",
  description: "Track leads captured and qualified by chatbots",
  purpose:
    "Show the lead generation effectiveness of chatbots with conversion metrics.",
  icon: "UserAddOutlined",
  color: "#ec4899",
  suggestedHtmlTemplateId: "performance-report",
  suggestedSubject: "{{leads_count}} new leads captured this week",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 1,
    time: "08:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Sales and marketing teams",
  expectedOutcomes: [
    "Lead pipeline visibility",
    "Conversion rate tracking",
    "Sales alignment",
    "Marketing ROI proof",
  ],
  bestPractices: [
    "Show lead quality breakdown",
    "Include conversion funnel",
    "Highlight hot leads",
    "Compare to targets",
  ],
  keyMetrics: [
    "Leads captured",
    "Qualification rate",
    "Conversion rate",
    "Lead sources",
    "Response time to leads",
  ],
  tags: ["custom", "leads", "sales", "conversion", "pipeline"],
  bestFor: ["Sales teams", "Marketing attribution", "Lead tracking"],
  difficulty: "intermediate",
  setupTime: "15 minutes",
  previewDescription: "Lead generation and conversion metrics",
};

export const CUSTOM_TEMPLATES = [
  ONBOARDING_PROGRESS,
  CHATBOT_ROI_REPORT,
  CONVERSATION_INSIGHTS,
  LEAD_GENERATION_REPORT,
];
